/**
 * Modal form for creating or editing a vendor credential.
 *
 * Supports SFTP with structured fields (the dominant transport at INVI's
 * scale today). Other transports fall through to a raw JSON textarea —
 * works for everything but loses validation. As specific transports
 * become common (REST, OAuth), they get their own structured branch.
 *
 * Edit mode: config fields default empty. Backend PATCH treats `config`
 * as optional — if the user doesn't fill it in, the stored config is
 * left untouched. Only when they fill in config does it re-encrypt and
 * replace.
 */
import React, { useState } from "react"

import {
  VendorCredential,
  createVendorCredential,
  updateVendorCredential,
} from "../../api/integrations"
import { THEME } from "../../config/theme"

// Source of truth: backend/services/vendor_file_service.py SUPPORTED_VENDORS.
// Mirror manually until we expose a /vendors/supported endpoint.
const SUPPORTED_VENDORS = [
  "tekion",
  "rapidrecon",
  "homenet",
  "stargard",
  "dealerconnect",
  "dealerdaily_toyota",
  "autoexact",
] as const

const TRANSPORT_TYPES = ["sftp", "rest", "mock"] as const
type TransportType = typeof TRANSPORT_TYPES[number]

const SCHEDULE_PRESETS = [
  { label: "Manual only", value: "" },
  { label: "Every hour",  value: "0 * * * *" },
  { label: "Every 4 hours", value: "0 */4 * * *" },
  { label: "Daily at 6 AM", value: "0 6 * * *" },
  { label: "Daily at midnight", value: "0 0 * * *" },
  { label: "Custom",      value: "__custom__" },
]

type Mode = "create" | "edit"

interface Props {
  mode: Mode
  credential: VendorCredential | null
  // The dealer group a newly created credential is tied to — comes from the
  // global switcher (the form has no group picker; that's the single source of
  // truth). null only in edit mode / "All Groups", where create is disabled.
  dealerGroupId: number | null
  onClose: () => void
  onSaved: () => void
}

const VendorCredentialForm: React.FC<Props> = ({
  mode,
  credential,
  dealerGroupId,
  onClose,
  onSaved,
}) => {
  const [vendorName, setVendorName] = useState(credential?.vendor_name ?? "tekion")
  const [transportType, setTransportType] = useState<TransportType>(
    (credential?.transport_type as TransportType) ?? "sftp",
  )
  // SFTP fields — empty by default, even in edit mode (config is encrypted server-side)
  const [sftpHost, setSftpHost] = useState("")
  const [sftpPort, setSftpPort] = useState("22")
  const [sftpUsername, setSftpUsername] = useState("")
  const [sftpPassword, setSftpPassword] = useState("")
  const [sftpRemotePath, setSftpRemotePath] = useState("")
  // Generic JSON config for non-SFTP transports
  const [configJson, setConfigJson] = useState("")

  // Schedule
  const initialCron = credential?.cron_expression ?? ""
  const matchingPreset = SCHEDULE_PRESETS.find((p) => p.value === initialCron)
  const [presetValue, setPresetValue] = useState(
    matchingPreset ? matchingPreset.value : (initialCron ? "__custom__" : ""),
  )
  const [customCron, setCustomCron] = useState(
    matchingPreset || !initialCron ? "" : initialCron,
  )
  const [isEnabled, setIsEnabled] = useState(credential?.is_enabled ?? true)

  // UI state
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buildConfig = (): Record<string, unknown> | null => {
    if (transportType === "sftp") {
      // In edit mode, allow leaving fields blank — backend skips config update if not sent.
      const allEmpty = !sftpHost && !sftpUsername && !sftpPassword && !sftpRemotePath
      if (mode === "edit" && allEmpty) return null
      const port = parseInt(sftpPort, 10)
      if (Number.isNaN(port)) {
        throw new Error("Port must be a number")
      }
      return {
        host: sftpHost,
        port,
        username: sftpUsername,
        password: sftpPassword,
        remote_path: sftpRemotePath,
      }
    }

    if (mode === "edit" && configJson.trim() === "") return null
    if (configJson.trim() === "") return {}
    try {
      return JSON.parse(configJson)
    } catch {
      throw new Error("Config must be valid JSON")
    }
  }

  const resolvedCron = (): string | null => {
    if (presetValue === "") return null  // Manual only
    if (presetValue === "__custom__") return customCron.trim() || null
    return presetValue
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const config = buildConfig()
      const cron = resolvedCron()
      if (mode === "create") {
        if (config === null) throw new Error("Configuration is required")
        if (dealerGroupId === null) {
          throw new Error("Select a dealer group before adding a credential")
        }
        await createVendorCredential({
          vendor_name: vendorName,
          transport_type: transportType,
          config,
          cron_expression: cron,
          is_enabled: isEnabled,
          dealer_group_id: dealerGroupId,
        })
      } else if (credential) {
        await updateVendorCredential(credential.id, {
          vendor_name: vendorName,
          transport_type: transportType,
          ...(config !== null ? { config } : {}),
          cron_expression: cron,
          is_enabled: isEnabled,
        })
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.lg,
          padding: "24px",
          width: "100%",
          maxWidth: "560px",
          maxHeight: "90vh",
          overflowY: "auto",
          fontFamily: THEME.fonts.primary,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        <div
          style={{
            fontSize: "20px",
            fontWeight: 500,
            marginBottom: "20px",
          }}
        >
          {mode === "create" ? "Add credential" : "Edit credential"}
        </div>

        <Field label="Vendor">
          <Select value={vendorName} onChange={setVendorName}>
            {SUPPORTED_VENDORS.map((v) => (
              <option key={v} value={v}>
                {formatVendorName(v)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Transport">
          <Select
            value={transportType}
            onChange={(v) => setTransportType(v as TransportType)}
          >
            {TRANSPORT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.toUpperCase()}
              </option>
            ))}
          </Select>
        </Field>

        {transportType === "sftp" ? (
          <>
            <Field label="Host" hint={mode === "edit" ? "Leave blank to keep existing" : undefined}>
              <Input value={sftpHost} onChange={setSftpHost} placeholder="sftp.vendor.com" />
            </Field>
            <Field label="Port">
              <Input value={sftpPort} onChange={setSftpPort} placeholder="22" />
            </Field>
            <Field label="Username">
              <Input value={sftpUsername} onChange={setSftpUsername} />
            </Field>
            <Field label="Password">
              <Input value={sftpPassword} onChange={setSftpPassword} type="password" autoComplete="off" />
            </Field>
            <Field label="Remote path" hint="e.g. /exports/inventory_*.csv">
              <Input value={sftpRemotePath} onChange={setSftpRemotePath} />
            </Field>
          </>
        ) : (
          <Field
            label="Config (JSON)"
            hint={mode === "edit" ? "Leave blank to keep existing" : "e.g. { \"api_key\": \"...\" }"}
          >
            <textarea
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              rows={6}
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: "13px",
                fontFamily: "ui-monospace, Menlo, monospace",
                border: `1px solid ${THEME.colors.silver}`,
                borderRadius: THEME.borderRadius.md,
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </Field>
        )}

        <Field label="Schedule">
          <Select value={presetValue} onChange={setPresetValue}>
            {SCHEDULE_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>

        {presetValue === "__custom__" && (
          <Field label="Custom cron" hint="five-field cron expression — e.g. 0 6 * * *">
            <Input value={customCron} onChange={setCustomCron} placeholder="0 6 * * *" />
          </Field>
        )}

        <Field label="Enabled">
          <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            Run on schedule (uncheck to pause without deleting)
          </label>
        </Field>

        {error && (
          <div
            style={{
              background: "rgba(220, 38, 38, 0.08)",
              color: THEME.colors.semantic.danger,
              border: "0.5px solid rgba(220, 38, 38, 0.3)",
              padding: "10px 14px",
              borderRadius: THEME.borderRadius.md,
              fontSize: "13px",
              marginTop: "12px",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "24px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={secondaryButtonStyle(submitting)}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={primaryButtonStyle(submitting)}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small leaf components (kept local — not reused elsewhere)
// ---------------------------------------------------------------------------

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div style={{ marginBottom: "14px" }}>
    <div
      style={{
        fontSize: "12px",
        fontWeight: 500,
        color: THEME.colors.neutral.light.textPrimary,
        marginBottom: "6px",
      }}
    >
      {label}
    </div>
    {children}
    {hint && (
      <div
        style={{
          fontSize: "11px",
          color: THEME.colors.neutral.light.textSecondary,
          marginTop: "4px",
        }}
      >
        {hint}
      </div>
    )}
  </div>
)

const Input: React.FC<{
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  autoComplete?: string
}> = ({ value, onChange, placeholder, type = "text", autoComplete }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoComplete={autoComplete}
    style={{
      width: "100%",
      padding: "8px 10px",
      fontSize: "13px",
      fontFamily: THEME.fonts.primary,
      border: `1px solid ${THEME.colors.silver}`,
      borderRadius: THEME.borderRadius.md,
      boxSizing: "border-box",
    }}
  />
)

const Select: React.FC<{
  value: string
  onChange: (v: string) => void
  children: React.ReactNode
}> = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: "100%",
      padding: "8px 10px",
      fontSize: "13px",
      fontFamily: THEME.fonts.primary,
      border: `1px solid ${THEME.colors.silver}`,
      borderRadius: THEME.borderRadius.md,
      boxSizing: "border-box",
      background: THEME.colors.neutral.light.surface,
    }}
  >
    {children}
  </select>
)

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  backgroundColor: THEME.colors.navy,
  color: THEME.colors.white,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: THEME.fonts.primary,
})

const secondaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  backgroundColor: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: THEME.fonts.primary,
})

const formatVendorName = (name: string): string =>
  name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

export default VendorCredentialForm
